// 地图状态管理工具
// 用于保存和恢复地图的缩放级别、中心位置和样式

import { isPlausibleCommandCoordinate } from './bogusGps';

class MapStateManager {
  constructor() {
    this.storageKey = 'map_state';
  }

  // 保存地图状态
  saveMapState(pageName, mapState) {
    try {
      // 指挥台：拒绝把境外/伪定位中心写入存档（否则刷新后飞美国）
      if (pageName === 'dashboard' && Array.isArray(mapState?.center) && mapState.center.length >= 2) {
        const lng = Number(mapState.center[0]);
        const lat = Number(mapState.center[1]);
        if (!isPlausibleCommandCoordinate(lat, lng)) {
          console.warn('拒绝保存境外/伪定位地图中心', lng, lat);
          return;
        }
      }
      const allStates = this.getAllMapStates();
      allStates[pageName] = {
        ...mapState,
        timestamp: Date.now()
      };
      localStorage.setItem(this.storageKey, JSON.stringify(allStates));
      console.log(`地图状态已保存: ${pageName}`, mapState);
    } catch (error) {
      console.error('保存地图状态失败:', error);
    }
  }

  // 获取地图状态
  getMapState(pageName) {
    try {
      const allStates = this.getAllMapStates();
      const state = allStates[pageName];
      
      if (state) {
        // 检查状态是否过期（24小时）
        const isExpired = Date.now() - state.timestamp > 24 * 60 * 60 * 1000;
        if (isExpired) {
          console.log(`地图状态已过期: ${pageName}`);
          this.clearMapState(pageName);
          return null;
        }

        // 指挥台：境外/伪定位中心直接丢弃
        if (pageName === 'dashboard' && Array.isArray(state.center) && state.center.length >= 2) {
          const lng = Number(state.center[0]);
          const lat = Number(state.center[1]);
          if (!isPlausibleCommandCoordinate(lat, lng)) {
            console.warn('丢弃境外/伪定位地图存档', lng, lat);
            this.clearMapState(pageName);
            return null;
          }
        }
        
        console.log(`地图状态已恢复: ${pageName}`, state);
        return state;
      }
      
      return null;
    } catch (error) {
      console.error('获取地图状态失败:', error);
      return null;
    }
  }

  // 获取所有地图状态
  getAllMapStates() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('获取所有地图状态失败:', error);
      return {};
    }
  }

  // 清除特定页面的地图状态
  clearMapState(pageName) {
    try {
      const allStates = this.getAllMapStates();
      delete allStates[pageName];
      localStorage.setItem(this.storageKey, JSON.stringify(allStates));
      console.log(`地图状态已清除: ${pageName}`);
    } catch (error) {
      console.error('清除地图状态失败:', error);
    }
  }

  // 清除所有地图状态
  clearAllMapStates() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('所有地图状态已清除');
    } catch (error) {
      console.error('清除所有地图状态失败:', error);
    }
  }

  // 获取默认地图状态
  getDefaultMapState() {
    return {
      center: [119.13643, 28.07741], // 龙泉市坐标
      zoom: 10,
      style: 'mapbox://styles/mapbox/satellite-streets-v12'
    };
  }

  /**
   * 将本地存档里的 style 转成 Mapbox 可用的样式。
   * 指挥系统存的是模式名（satellite/streets/outdoors），不是 mapbox:// URL；
   * 若直接 setStyle('satellite') 会请求 localhost:3000/satellite → 404 白图。
   */
  resolveUsableMapStyle(style) {
    const fallback = 'mapbox://styles/mapbox/satellite-streets-v12';
    if (style && typeof style === 'object') return style;
    if (typeof style !== 'string' || !style.trim()) return fallback;

    const value = style.trim();
    if (value.startsWith('mapbox://')) return value;
    if (/^https?:\/\//i.test(value)) return value;

    const modeMap = {
      streets: 'mapbox://styles/mapbox/streets-v11',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
      outdoors: 'mapbox://styles/mapbox/outdoors-v12',
      'satellite-streets': fallback,
      'satellite_streets': fallback
    };
    if (modeMap[value]) return modeMap[value];

    // 旧相对路径 / 图商 id（如 satellite_tms、gaode-img）一律回退
    console.warn('忽略无效地图样式存档，改用默认 Mapbox 样式:', value);
    return fallback;
  }
}

// 创建单例实例
const mapStateManager = new MapStateManager();

export default mapStateManager; 